import { ArrowRightIcon, LinkIcon, BoltIcon, LockClosedIcon } from '@heroicons/react/24/outline'
import { SparklesIcon } from '@heroicons/react/24/solid'
import { useNavigate } from 'react-router-dom'
import { useEffect } from 'react'

export function AboutPage() {
  const navigate = useNavigate()

  useEffect(() => {
    // Title
    document.title = 'About JobTrackr.it - Meet the Founder Behind the Job Application Tracker'
    
    // Meta Description
    let metaDesc = document.querySelector('meta[name="description"]')
    if (!metaDesc) {
      metaDesc = document.createElement('meta')
      metaDesc.setAttribute('name', 'description')
      document.head.appendChild(metaDesc)
    }
    metaDesc.setAttribute('content', 'Meet Patryk Domasik, founder of JobTrackr.it. Learn how a personal struggle with job application spreadsheets became the solution thousands of job seekers trust today.')
    
    // Canonical
    let canonical = document.querySelector('link[rel="canonical"]')
    if (!canonical) {
      canonical = document.createElement('link')
      canonical.setAttribute('rel', 'canonical')
      document.head.appendChild(canonical)
    }
    canonical.setAttribute('href', 'https://jobtrackr.it/about')
    
    // OG Tags
    const ogTags = [
      { property: 'og:title', content: 'About JobTrackr.it - Meet the Founder' },
      { property: 'og:description', content: 'Learn how JobTrackr.it was built by someone who experienced job search chaos firsthand.' },
      { property: 'og:url', content: 'https://jobtrackr.it/about' },
      { property: 'og:type', content: 'website' },
      { property: 'og:image', content: 'https://jobtrackr.it/og-image-about.png' },
    ]
    
    ogTags.forEach(({ property, content }) => {
      let tag = document.querySelector(`meta[property="${property}"]`)
      if (!tag) {
        tag = document.createElement('meta')
        tag.setAttribute('property', property)
        document.head.appendChild(tag)
      }
      tag.setAttribute('content', content)
    })
    
    // Twitter Cards
    const twitterTags = [
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: 'About JobTrackr.it - Meet the Founder' },
      { name: 'twitter:description', content: 'Built by someone who experienced job search chaos firsthand' },
      { name: 'twitter:image', content: 'https://jobtrackr.it/og-image-about.png' },
    ]
    
    twitterTags.forEach(({ name, content }) => {
      let tag = document.querySelector(`meta[name="${name}"]`)
      if (!tag) {
        tag = document.createElement('meta')
        tag.setAttribute('name', name)
        document.head.appendChild(tag)
      }
      tag.setAttribute('content', content)
    })
  }, [])

  return (
    <div className="min-h-screen bg-none bg-app-page">
      {/* Decorative grid overlay */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#0a0f1a_1px,transparent_1px),linear-gradient(to_bottom,#0a0f1a_1px,transparent_1px)] bg-[size:4rem_4rem] opacity-20 hidden" />
      
      {/* Navigation */}
      <nav className="relative z-10 border-b border-app-line/50 backdrop-blur-sm">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-app-muted hover:text-app-accent transition-colors group"
          >
            <svg className="w-8 h-8 transform group-hover:-translate-x-1 transition-transform" viewBox="0 0 24 24" fill="none">
              <path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <span className="font-medium">Back to JobTrackr.it</span>
          </button>
          <a
            href="https://thedomas.dev"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-app-surface/50 border border-app-line hover:border-app-accent hover:bg-app-surface text-app-muted hover:text-app-ink transition-all"
          >
            <LinkIcon className="w-5 h-5" />
            <span className="text-sm font-medium">Author</span>
          </a>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative z-10 max-w-4xl mx-auto px-6 pt-20 pb-16">
        <div className="inline-block mb-6">
          <span className="px-4 py-2 rounded-full text-xs font-semibold tracking-wider uppercase border border-sky-500/20 text-sky-700 dark:text-app-accent bg-none bg-app-highlight">
            About JobTrackr.it
          </span>
        </div>
        
        <h1 className="text-5xl md:text-7xl font-bold mb-8 leading-tight font-display">
          <span className="bg-none text-app-ink">
            Built by someone who's been there
          </span>
        </h1>
        
        <p className="text-xl md:text-2xl text-app-muted leading-relaxed max-w-3xl">
          JobTrackr.it started from a simple frustration: losing track of job applications in a messy spreadsheet. 
          What began as a personal tool became the solution thousands of job seekers needed.
        </p>
      </section>

      {/* Story Section */}
      <section className="relative z-10 max-w-4xl mx-auto px-6 py-16">
        <div className="grid md:grid-cols-5 gap-12 items-start">
          <div className="md:col-span-2">
            <div className="sticky top-8">
              <h2 className="text-3xl font-bold text-app-ink mb-4 font-display">The Problem</h2>
              <div className="w-16 h-1 rounded-full bg-none bg-app-primary" />
            </div>
          </div>
          
          <div className="md:col-span-3 space-y-6 text-lg text-app-muted leading-relaxed">
            <p>
              In early 2024, I found myself applying to dozens of positions. I started with a spreadsheet – columns for company names, 
              dates, statuses, salary ranges. It worked... until it didn't.
            </p>
            <p>
              I'd forget which stage I was in. Miss follow-up deadlines. Compare offers in different currencies and struggle with the math. 
              Wonder which applications even got opened. The stress of the job search was compounded by the chaos of tracking it all.
            </p>
            <p className="italic text-app-muted border-l-4 border-red-500 pl-6 py-2">
              "There has to be a better way than manually updating spreadsheet cells at midnight."
            </p>
          </div>
        </div>
      </section>

      {/* Solution Section */}
      <section className="relative z-10 max-w-4xl mx-auto px-6 py-16">
        <div className="grid md:grid-cols-5 gap-12 items-start">
          <div className="md:col-span-2">
            <div className="sticky top-8">
              <h2 className="text-3xl font-bold text-app-ink mb-4 font-display">The Solution</h2>
              <div className="w-16 h-1 rounded-full bg-none bg-app-primary" />
            </div>
          </div>
          
          <div className="md:col-span-3 space-y-6 text-lg text-app-muted leading-relaxed">
            <p>
              I built JobTrackr.it for myself first. Paste a job URL, and it extracts the details automatically. Visual pipeline to see 
              every stage at a glance. Reminders so nothing falls through the cracks. Multi-currency conversion because I was applying 
              internationally.
            </p>
            <p>
              Then I added AI resume analysis – because after getting ghosted by ATS systems, I realized my resume wasn't optimized for 
              the keywords recruiters were searching for. One tweak session later, my callback rate jumped.
            </p>
            <p>
              What surprised me most wasn't the features – it was the clarity. Seeing everything in one place, organized and trackable, 
              removed the mental overhead. I could focus on interview prep instead of "wait, did I apply there already?"
            </p>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="relative z-10 max-w-4xl mx-auto px-6 py-16">
        <div className="bg-app-surface/50 backdrop-blur-sm border border-app-line rounded-2xl p-8 md:p-12">
          <h2 className="text-3xl font-bold text-app-ink mb-8 font-display">What JobTrackr.it Stands For</h2>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div>
              <div className="w-12 h-12 rounded-xl border border-red-500/30 flex items-center justify-center mb-4 bg-none bg-app-highlight">
                <SparklesIcon className="w-6 h-6 text-red-700 dark:text-red-400" />
              </div>
              <h3 className="text-xl font-bold text-app-ink mb-3 font-display">Focused</h3>
              <p className="text-app-muted">
                No bloated features. Every function serves the job search – nothing more, nothing less.
              </p>
            </div>

            <div>
              <div className="w-12 h-12 rounded-xl border border-sky-500/30 flex items-center justify-center mb-4 bg-none bg-app-highlight">
                <BoltIcon className="w-6 h-6 text-sky-700 dark:text-app-accent" />
              </div>
              <h3 className="text-xl font-bold text-app-ink mb-3 font-display">Fast</h3>
              <p className="text-app-muted">
                Instant job extraction. One-click updates. No waiting around – your time matters.
              </p>
            </div>

            <div>
              <div className="w-12 h-12 rounded-xl border border-cyan-500/30 flex items-center justify-center mb-4 bg-none bg-app-highlight">
                <LockClosedIcon className="w-6 h-6 text-cyan-700 dark:text-cyan-400" />
              </div>
              <h3 className="text-xl font-bold text-app-ink mb-3 font-display">Private</h3>
              <p className="text-app-muted">
                Your job search data is yours. We don't sell it, share it, or use it for anything but helping you get hired.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Founder Section */}
      <section className="relative z-10 max-w-4xl mx-auto px-6 py-16">
        <div className="grid md:grid-cols-5 gap-12 items-start">
          <div className="md:col-span-2">
            <div className="sticky top-8">
              <h2 className="text-3xl font-bold text-app-ink mb-4 font-display">Who Built This</h2>
              <div className="w-16 h-1 rounded-full bg-none bg-app-primary" />
            </div>
          </div>
          
          <div className="md:col-span-3">
            <div className="backdrop-blur-sm border border-app-line rounded-2xl p-4 sm:p-8 bg-none bg-app-surface">
              <div className="flex flex-col sm:flex-row items-start gap-4 sm:gap-6 mb-6">
                <img 
                  src="/patryk-photo.webp" 
                  alt="Patryk Domasik - Founder of JobTrackr.it"
                  className="w-full sm:w-40 md:w-48 lg:w-60 h-auto sm:h-40 md:h-48 lg:h-60 rounded-2xl object-cover flex-shrink-0 border border-app-line"
                />
                <div className="w-full">
                  <h3 className="text-xl sm:text-2xl font-bold text-app-ink mb-2 font-display">Patryk Domasik</h3>
                  <p className="text-sky-700 dark:text-app-accent font-medium mb-3">Founder & Developer</p>
                  <a
                    href="https://thedomas.dev"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-app-muted hover:text-app-accent transition-colors group"
                  >
                    <LinkIcon className="w-4 h-4" />
                    <span className="text-sm font-medium group-hover:underline">thedomas.dev</span>
                    <ArrowRightIcon className="w-4 h-4 transform group-hover:translate-x-1 transition-transform" />
                  </a>
                </div>
              </div>
              
              <div className="space-y-4 text-app-muted leading-relaxed">
                <p>
                  I'm a full-stack developer with a background in building SaaS products. I've been on both sides of the hiring 
                  table – interviewing candidates and being interviewed myself.
                </p>
                <p>
                  JobTrackr.it is my answer to a problem I experienced firsthand: the job search process is stressful enough without 
                  losing track of where you stand with each opportunity. This tool exists to remove that friction.
                </p>
                <p>
                  I maintain JobTrackr.it full-time, respond to user feedback personally, and ship improvements weekly. If you have 
                  questions or suggestions, I'm just an email away.
                </p>
              </div>

              <div className="mt-6 pt-6 border-t border-app-line">
                <p className="text-sm text-app-muted">
                  Built with: React, TypeScript, Supabase, OpenAI • Hosted on Vercel • Based in Poland 🇵🇱
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="relative z-10 max-w-4xl mx-auto px-6 py-16">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          <div className="text-center">
            <div className="text-4xl md:text-5xl font-bold mb-2 bg-none text-app-ink">
              1,000+
            </div>
            <div className="text-sm text-app-muted uppercase tracking-wide">Active Users</div>
          </div>
          <div className="text-center">
            <div className="text-4xl md:text-5xl font-bold mb-2 bg-none text-app-ink">
              5,000+
            </div>
            <div className="text-sm text-app-muted uppercase tracking-wide">Apps Tracked</div>
          </div>
          <div className="text-center">
            <div className="text-4xl md:text-5xl font-bold mb-2 bg-none text-app-ink">
              50+
            </div>
            <div className="text-sm text-app-muted uppercase tracking-wide">Countries</div>
          </div>
          <div className="text-center">
            <div className="text-4xl md:text-5xl font-bold mb-2 bg-none text-app-ink">
              2026
            </div>
            <div className="text-sm text-app-muted uppercase tracking-wide">Founded</div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative z-10 max-w-4xl mx-auto px-6 py-20">
        <div className="relative overflow-hidden rounded-3xl p-12 text-center bg-none bg-app-highlight">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_120%,rgba(255,255,255,0.1),transparent_50%)] hidden" />
          
          <div className="relative z-10">
            <h2 className="text-3xl md:text-4xl font-bold text-app-ink mb-4 font-display">
              Ready to organize your job search?
            </h2>
            <p className="text-xl text-sky-700 dark:text-app-accent mb-8 max-w-2xl mx-auto">
              Join thousands of job seekers who've taken control of their applications.
            </p>
            
            <button
              onClick={() => navigate('/')}
              className="inline-flex items-center gap-2 px-8 py-4 bg-app-primary text-white font-bold text-lg hover:bg-app-primary-hover transition-all group rounded-lg shadow-app-card hover:shadow-app-hover hover:scale-100"
            >
              <span>Get Started Free</span>
              <ArrowRightIcon className="w-5 h-5 transform group-hover:translate-x-1 transition-transform" />
            </button>
            
            <p className="text-sm text-sky-700 dark:text-app-accent mt-4">
              No credit card required • 10 applications free forever
            </p>
          </div>
        </div>
      </section>

      {/* Footer spacer */}
      <div className="h-16" />
    </div>
  )
}
